/**
 * ReelsSnapshotModal Component
 * 
 * OPARA v30.0 - Admin Dashboard Reels Snapshot (Detaylı Veri Tablosu)
 * - Aynı influencer için birden fazla analiz görüntüleme
 * - Tarih seçici ile farklı analizler arasında geçiş
 * 
 * 12 reel'ı detaylı gösteren modal:
 * - Shortcode, Tarih, View, Like, Comment, Pinned, Marka
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Loader2, Pin, Eye, Heart, MessageCircle, Calendar, Tag, AlertTriangle, Info, X, Clock } from "lucide-react";
import axios from 'axios';
import { BACKEND_URL } from "@/utils/apiConfig";
import { toast } from 'sonner';
import { useTranslation } from "@/hooks/useTranslation";

// Format large numbers
const formatNumber = (num) => {
  if (num == null || num === -1) return '-';
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString('tr-TR');
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr || dateStr === 'N/A') return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Format datetime (for analysis selector) - UTC'yi yerel saate çevirir
const formatDateTime = (dateStr) => {
  if (!dateStr || dateStr === 'N/A') return 'N/A';
  try {
    const date = new Date(dateStr);
    // Tarayıcının yerel saat dilimini al
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formatted = date.toLocaleString('tr-TR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timeZone
    });
    // Saat dilimi kısaltmasını ekle
    const tzAbbr = new Date().toLocaleTimeString('tr-TR', { timeZoneName: 'short' }).split(' ').pop();
    return `${formatted} (${tzAbbr})`;
  } catch {
    return dateStr;
  }
};

// v30.0: Format duration (seconds to "X dk Y sn")
// v30.6: Ondalık sayıları yuvarla (45.65... → 45 sn)
const formatDuration = (seconds) => {
  if (seconds == null || seconds === undefined) return null;
  
  const totalSeconds = Math.round(seconds); // Ondalıkları yuvarla
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  
  if (mins > 0) {
    return `${mins} dk ${secs} sn`;
  }
  return `${secs} sn`;
};

export default function ReelsSnapshotModal({ 
  isOpen, 
  onClose, 
  username, 
  reelsSnapshot: propsSnapshot = null
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [reelsData, setReelsData] = useState(null);
  const [error, setError] = useState(null);
  const [availableAnalyses, setAvailableAnalyses] = useState([]);
  const [selectedQueryId, setSelectedQueryId] = useState(null);
  const [currentQueryTime, setCurrentQueryTime] = useState(null);
  const [currentDuration, setCurrentDuration] = useState(null);  // v30.0
  const [anatomy, setAnatomy] = useState(null);  // v31 ANATOMY-MERGE (analysis_tasks)
  const [failedAnalysis, setFailedAnalysis] = useState(null);  // v31 Commit-3: FAILED sebep-karti

  useEffect(() => {
    if (isOpen) {
      // Reset state
      setAvailableAnalyses([]);
      setSelectedQueryId(null);
      setCurrentQueryTime(null);
      setCurrentDuration(null);  // v30.0
      
      // Props'tan gelen snapshot varsa kullan, yoksa API'den çek
      if (propsSnapshot && Array.isArray(propsSnapshot) && propsSnapshot.length > 0) {
        setReelsData(propsSnapshot);
        setError(null);
      } else if (username) {
        fetchReelsSnapshot();
      }
    }
  }, [isOpen, username, propsSnapshot]);

  const fetchReelsSnapshot = async (queryId = null) => {
    if (!username) return;
    
    setLoading(true);
    setError(null);
    setAnatomy(null);  // v31: onceki analiz anatomisini temizle
    setFailedAnalysis(null);  // v31 Commit-3: onceki fail-karti temizle

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const cleanUsername = username.toLowerCase().replace('@', '').trim();
      
      // Query ID varsa URL'e ekle
      let url = `${BACKEND_URL}/api/admin/reels-snapshot/${cleanUsername}`;
      if (queryId) {
        url += `?query_id=${queryId}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Available analyses'i sakla
      if (response.data.available_analyses) {
        setAvailableAnalyses(response.data.available_analyses);
      }
      
      // Current query bilgilerini sakla
      if (response.data.query_id) {
        setSelectedQueryId(response.data.query_id);
      }
      if (response.data.query_time) {
        setCurrentQueryTime(response.data.query_time);
      }
      // v30.0: Analiz süresini kaydet
      if (response.data.analysis_duration !== undefined) {
        setCurrentDuration(response.data.analysis_duration);
      }
      // v31 ANATOMY-MERGE: anatomi-ozeti (null olabilir -> UI eski-analiz notu gosterir)
      setAnatomy(response.data.anatomy || null);
      // v31 Commit-3: FAILED analiz -> sebep-karti (reels_snapshot yok, reel-tablo cizilmez)
      setFailedAnalysis(response.data.failed_analysis || null);
      
      if (response.data.reels_snapshot) {
        setReelsData(response.data.reels_snapshot);
      } else if (response.data.warning) {
        setError(response.data.warning);
      }
    } catch (err) {
      console.error('Reels snapshot fetch error:', err);
      if (err.response?.status === 404) {
        setError(t('reels.error_not_found'));
      } else if (err.response?.status === 401) {
        setError(t('reels.error_admin_only'));
      } else {
        setError(t('reels.error_generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnalysisChange = (queryId) => {
    setSelectedQueryId(queryId);
    // v30.0: Seçilen analizin duration'ını güncelle
    const selectedAnalysis = availableAnalyses.find(a => a.query_id === queryId);
    if (selectedAnalysis) {
      setCurrentDuration(selectedAnalysis.analysis_duration);
    }
    fetchReelsSnapshot(queryId);
  };

  const handleOpenReel = (shortcode) => {
    window.open(`https://www.instagram.com/reel/${shortcode}/`, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-[1600px] max-h-[90vh] overflow-hidden bg-gray-900 border-gray-700 [&>button]:text-white [&>button]:hover:text-white [&>button]:hover:bg-gray-700">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <Eye className="w-5 h-5 text-yellow-400" />
            {t('reels.title')}
          </DialogTitle>
          
          {/* Analiz Bilgi Kutusu - Tek kutuda tüm bilgiler */}
          <div className="mt-4 p-4 bg-gray-800 rounded-lg">
            <div className="flex items-center justify-between">
              {/* Sol: Influencer + Tarih + Yapan */}
              <div className="flex flex-col gap-2">
                {/* Influencer */}
                <div className="flex items-center gap-2">
                  <span className="text-yellow-400 font-bold text-lg">{t('reels.influencer_label')} @{username?.replace('@', '')}</span>
                </div>
                
                {/* Tarih seçici (birden fazla analiz varsa) */}
                {availableAnalyses.length > 1 && (
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-yellow-400" />
                    <span className="text-gray-400 text-sm">{t('reels.analysis_date')}</span>
                    <Select value={selectedQueryId || ''} onValueChange={handleAnalysisChange}>
                      <SelectTrigger className="w-[220px] bg-gray-700 border-gray-600 text-white h-8 text-sm">
                        <SelectValue placeholder={t('reels.select_analysis_placeholder')} />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-600">
                        {availableAnalyses.map((analysis, idx) => (
                          <SelectItem 
                            key={analysis.query_id || idx} 
                            value={analysis.query_id}
                            className="text-white hover:bg-gray-700"
                          >
                            {formatDateTime(analysis.query_time)}
                            {idx === 0 && <span className="ml-2 text-yellow-400 text-xs">{t('reels.latest')}</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-gray-500 text-xs">({t('reels.analyses_available', { count: availableAnalyses.length })})</span>
                  </div>
                )}
                
                {/* Tek analiz varsa sadece tarih */}
                {availableAnalyses.length === 1 && currentQueryTime && (
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-yellow-400" />
                    <span className="text-gray-400 text-sm">{t('reels.analysis_date')}</span>
                    <span className="text-white text-sm">{formatDateTime(currentQueryTime)}</span>
                  </div>
                )}
                
                {/* Analizi yapan kişi */}
                {(availableAnalyses.length > 0) && (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span>{t('reels.analyzed_by')}</span>
                    <span className="text-white">
                      {availableAnalyses.find(a => a.query_id === selectedQueryId)?.user_name || 
                       availableAnalyses[0]?.user_name || t('reels.unknown')}
                    </span>
                    {(availableAnalyses.find(a => a.query_id === selectedQueryId)?.user_email || 
                      availableAnalyses[0]?.user_email) && (
                      <span className="text-gray-500">
                        ({availableAnalyses.find(a => a.query_id === selectedQueryId)?.user_email || 
                          availableAnalyses[0]?.user_email})
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              {/* Sağ: Analiz Süresi + Süreç Breakdown */}
              <div className="flex flex-col items-end gap-2">
                {currentDuration != null ? (
                  <div className="px-3 py-2 bg-green-500/20 rounded-lg">
                    <span className="text-green-400 text-xs">{t('reels.analysis_duration')}</span>
                    <div className="text-green-400 font-bold text-lg">{formatDuration(currentDuration)}</div>
                  </div>
                ) : !loading && (
                  <div className="px-3 py-2 bg-gray-700/50 rounded-lg">
                    <span className="text-gray-400 text-xs">{t('reels.analysis_duration')}</span>
                    <div className="text-gray-400 text-sm">{t('reels.not_measured')}</div>
                  </div>
                )}
                {/* v31 ANATOMY-MERGE: analiz anatomi-ozeti (rozetler + faz-sureleri); anatomy=null -> eski-analiz notu */}
                {anatomy ? (
                  <div className="px-3 py-2 bg-gray-800/80 rounded-lg text-xs">
                    {/* Sonuc + Provider + K1 + Begeni-Gizli + Yetersiz-Veri rozetleri */}
                    <div className="flex flex-wrap items-center gap-1.5 justify-end">
                      {anatomy.status && (
                        <span className={`px-2 py-0.5 rounded font-semibold ${anatomy.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                          {anatomy.status === 'failed' ? `FAIL${anatomy.error_type ? ' · ' + anatomy.error_type : ''}` : 'OK'}
                        </span>
                      )}
                      {anatomy.provider && (
                        <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">{anatomy.provider}</span>
                      )}
                      {/* v31 Commit-3: provider-zinciri detayi (result.anatomy.provider_chain). discovery_source None -> BD (canli-teyitli). */}
                      {anatomy.provider_chain && (
                        <>
                          <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-300" title="Discovery provider">
                            disc: {anatomy.provider_chain.discovery_source || 'BD'}
                          </span>
                          {anatomy.provider_chain.discovery_win_region && (
                            <span className="px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300" title="Discovery kazanan bolge">
                              win: {anatomy.provider_chain.discovery_win_region}
                            </span>
                          )}
                          {anatomy.provider_chain.bayrak_triggered && (
                            <span className="px-2 py-0.5 rounded bg-teal-500/20 text-teal-300" title="BAYRAK YARISI (SF single-reel enrichment) tetiklendi">BAYRAK</span>
                          )}
                          {/* v31 Commit-2: SF-enrichment reel sayisi (BAYRAK yaninda) */}
                          {anatomy.provider_chain.enriched_count > 0 && (
                            <span className="px-2 py-0.5 rounded bg-teal-500/20 text-teal-300" title="BAYRAK SF single-reel enrichment ile zenginlestirilen reel sayisi">SF: {anatomy.provider_chain.enriched_count}</span>
                          )}
                          {/* v31 Commit-2: RESCUE kosullu-detay (rescued/attempted -> inherit). Eski kayitta sayilar yok -> sade "RESCUE". */}
                          {anatomy.provider_chain.rescue_triggered && (
                            <span className="px-2 py-0.5 rounded bg-orange-500/20 text-orange-300" title="BD Rescue (kurtarilan/denenen -> miras-bolge)">
                              RESCUE{anatomy.provider_chain.rescue_attempted ? ` ${anatomy.provider_chain.rescued_count ?? 0}/${anatomy.provider_chain.rescue_attempted}` : ''}{anatomy.provider_chain.rescue_inherit_region ? ` -> ${anatomy.provider_chain.rescue_inherit_region}` : ''}
                            </span>
                          )}
                        </>
                      )}
                      {anatomy.meta_decoy && anatomy.meta_decoy.durum_b_triggered && (
                        <span className="px-2 py-0.5 rounded bg-pink-500/20 text-pink-300" title="META_DECOY tutarlilik-dongusu (BD re-scrape) tetiklendi">META_DECOY</span>
                      )}
                      {anatomy.removed_by_floor_ceiling > 0 && (
                        <span className="px-2 py-0.5 rounded bg-orange-500/20 text-orange-400">K1: {anatomy.removed_by_floor_ceiling}</span>
                      )}
                      {/* v31 Commit-2: MEDYAN-CAP rozeti (forecast tavani medyana cekildi). applied degilse cikmaz. */}
                      {anatomy.medyan_cap?.applied && (
                        <span className="px-2 py-0.5 rounded bg-orange-500/20 text-orange-400" title={`forecast ${anatomy.medyan_cap.forecast} -> capped ${anatomy.medyan_cap.capped} (medyan ${anatomy.medyan_cap.median})`}>MEDYAN-CAP</span>
                      )}
                      {anatomy.all_likes_hidden && (
                        <span className="px-2 py-0.5 rounded bg-gray-600/40 text-gray-300" title={anatomy.hidden_likes_error || ''}>Beğeni Gizli</span>
                      )}
                      {anatomy.insufficient_data && (
                        <span className="px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400">Yetersiz Veri</span>
                      )}
                    </div>
                    {/* v31 Commit-4: Discovery deneme-zinciri (fail'ler -> win). discovery_attempts bos + win = ilk denemede tuttu. */}
                    {anatomy.provider_chain && (anatomy.provider_chain.discovery_win_region || (anatomy.provider_chain.discovery_attempts && anatomy.provider_chain.discovery_attempts.length > 0)) && (
                      <div className="text-xs text-gray-400 mt-1 flex items-center gap-1 flex-wrap justify-end">
                        <span className="text-gray-500">Kesif:</span>
                        {(anatomy.provider_chain.discovery_attempts || []).map((a, i) => (
                          <span key={i} className="text-red-400/70">
                            {a.region} <span className="opacity-60">x{a.wall_category || '?'}</span>
                            <span className="text-gray-500 mx-0.5">-&gt;</span>
                          </span>
                        ))}
                        {anatomy.provider_chain.discovery_win_region && (
                          <span className="text-green-400">{anatomy.provider_chain.discovery_win_region} OK</span>
                        )}
                        {(!anatomy.provider_chain.discovery_attempts || anatomy.provider_chain.discovery_attempts.length === 0) && anatomy.provider_chain.discovery_win_region && (
                          <span className="text-gray-500">(ilk denemede)</span>
                        )}
                      </div>
                    )}
                    {/* Faz-sureleri (stage_times) - dormant hook artik analysis_tasks'tan besleniyor */}
                    {anatomy.stage_times && (
                      <div className="flex flex-wrap gap-2 justify-end mt-1.5">
                        {anatomy.stage_times.BD_DISCOVERY != null && (
                          <span className="text-blue-400">Faz-1: {formatDuration(anatomy.stage_times.BD_DISCOVERY)}</span>
                        )}
                        {anatomy.stage_times.SF_ENRICHMENT != null && (
                          <span className="text-purple-400">SF: {formatDuration(anatomy.stage_times.SF_ENRICHMENT)}</span>
                        )}
                        {anatomy.stage_times.CORE_ANALYSIS != null && (
                          <span className="text-cyan-400">Core: {formatDuration(anatomy.stage_times.CORE_ANALYSIS)}</span>
                        )}
                      </div>
                    )}
                  </div>
                ) : !loading && (
                  <div className="px-3 py-2 bg-gray-700/30 rounded-lg text-xs text-gray-500">
                    Anatomi verisi yok (eski analiz)
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="overflow-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
              <span className="ml-3 text-gray-400">{t('reels.loading_data')}</span>
            </div>
          ) : failedAnalysis ? (
            /* v31 Commit-3: FAILED analiz sebep-karti (reel-tablo yerine) */
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center gap-3">
              <AlertTriangle className="w-12 h-12 text-red-500" />
              <div className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400 font-bold text-lg">
                ANALİZ BAŞARISIZ{failedAnalysis.error_type ? ` · ${failedAnalysis.error_type}` : ''}
              </div>
              {failedAnalysis.wall_category && (
                <div className="text-sm text-gray-400">Kategori: <span className="text-gray-200">{failedAnalysis.wall_category}</span></div>
              )}
              {failedAnalysis.user_message && (
                <p className="text-gray-300 max-w-lg">{failedAnalysis.user_message}</p>
              )}
              {failedAnalysis.stage_times && (
                <div className="flex flex-wrap gap-2 justify-center text-xs mt-2">
                  <span className="text-gray-500">Nereye kadar geldi:</span>
                  {failedAnalysis.stage_times.BD_DISCOVERY != null && (
                    <span className="text-blue-400">Faz-1: {formatDuration(failedAnalysis.stage_times.BD_DISCOVERY)}</span>
                  )}
                  {failedAnalysis.stage_times.SF_ENRICHMENT != null && (
                    <span className="text-purple-400">SF: {formatDuration(failedAnalysis.stage_times.SF_ENRICHMENT)}</span>
                  )}
                  {failedAnalysis.stage_times.CORE_ANALYSIS != null && (
                    <span className="text-cyan-400">Core: {formatDuration(failedAnalysis.stage_times.CORE_ANALYSIS)}</span>
                  )}
                </div>
              )}
              {failedAnalysis.created_at && (
                <div className="text-xs text-gray-500">{formatDateTime(failedAnalysis.created_at)}</div>
              )}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
              <p className="text-gray-400 mb-2">{error}</p>
              <p className="text-sm text-gray-500">
                {t('reels.old_analysis_note')}
              </p>
            </div>
          ) : reelsData && reelsData.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700 hover:bg-gray-800/50">
                  <TableHead className="text-gray-400 font-semibold w-12">#</TableHead>
                  <TableHead className="text-gray-400 font-semibold">Shortcode</TableHead>
                  <TableHead className="text-gray-400 font-semibold">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {t('reels.col_date')}
                    </div>
                  </TableHead>
                  {/* v30.19: Owner kolonu */}
                  <TableHead className="text-gray-400 font-semibold">
                    <div className="flex items-center gap-1">
                      Owner
                    </div>
                  </TableHead>
                  {/* v30.19: Co-author kolonu */}
                  <TableHead className="text-gray-400 font-semibold">
                    <div className="flex items-center gap-1">
                      Co-author
                    </div>
                  </TableHead>
                  <TableHead className="text-gray-400 font-semibold text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Eye className="w-3 h-3" /> View
                    </div>
                  </TableHead>
                  <TableHead className="text-gray-400 font-semibold text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Heart className="w-3 h-3" /> Like
                    </div>
                  </TableHead>
                  <TableHead className="text-gray-400 font-semibold text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <MessageCircle className="w-3 h-3" /> {t('reels.col_comment')}
                    </div>
                  </TableHead>
                  <TableHead className="text-gray-400 font-semibold text-center">
                    <div className="flex items-center gap-1 justify-center">
                      <Pin className="w-3 h-3" /> Pin
                    </div>
                  </TableHead>
                  <TableHead className="text-gray-400 font-semibold">
                    <div className="flex items-center gap-1">
                      <Tag className="w-3 h-3" /> {t('reels.col_brand')}
                    </div>
                  </TableHead>
                  {/* v31 Commit-3: Katman-eleme kolonu (result.anatomy) */}
                  <TableHead className="text-gray-400 font-semibold text-center">
                    <div className="flex items-center gap-1 justify-center">
                      Eleme
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reelsData.map((reel, idx) => (
                  <TableRow 
                    key={reel.shortcode || idx} 
                    className={`border-gray-700 hover:bg-gray-800/50 ${reel.is_collab ? 'bg-yellow-500/5' : ''}`}
                  >
                    <TableCell className="text-gray-300 font-mono">
                      {reel.position || idx + 1}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-blue-400 hover:text-blue-300 p-0 h-auto font-mono text-xs"
                        onClick={() => handleOpenReel(reel.shortcode)}
                      >
                        {reel.shortcode?.substring(0, 11)}...
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-gray-300 text-sm">
                      <div className="flex items-center gap-1">
                        {formatDate(reel.date)}
                        {reel.date_source === 'HTML_TIME' && (
                          <span title={t('reels.date_src_html')} className="text-green-400 text-xs">✓</span>
                        )}
                        {reel.date_source === 'JSON_TIMESTAMP' && (
                          <span title={t('reels.date_src_json')} className="text-gray-400 text-xs">✓</span>
                        )}
                        {reel.date_source === 'MISSING' && (
                          <span title={t('reels.date_src_missing')} className="text-yellow-400 text-xs">⚠</span>
                        )}
                      </div>
                    </TableCell>
                    {/* v30.19: Owner kolonu */}
                    <TableCell className="text-gray-300 text-sm">
                      {reel.owner_username ? (
                        <span className="text-cyan-400">@{reel.owner_username}</span>
                      ) : (
                        <span className="text-gray-500">n/a</span>
                      )}
                    </TableCell>
                    {/* v30.19: Co-author kolonu */}
                    <TableCell className="text-gray-300 text-sm">
                      {reel.coauthors && reel.coauthors.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {reel.coauthors.slice(0, 2).map((coauthor, cIdx) => (
                            <span key={cIdx} className="text-purple-400 text-xs">
                              @{typeof coauthor === 'string' ? coauthor : coauthor.username || coauthor}
                            </span>
                          ))}
                          {reel.coauthors.length > 2 && (
                            <span className="text-gray-500 text-xs">+{reel.coauthors.length - 2}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">n/a</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-gray-300 font-semibold">
                      {formatNumber(reel.view_count)}
                    </TableCell>
                    <TableCell className="text-right">
                      {reel.like_count === -1 ? (
                        <span className="text-gray-500 text-xs">{t('reels.hidden')}</span>
                      ) : (
                        <span className="text-gray-300 font-semibold">{formatNumber(reel.like_count)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-gray-300">
                      {formatNumber(reel.comment_count)}
                    </TableCell>
                    <TableCell className="text-center">
                      {reel.is_pinned ? (
                        <Badge variant="secondary" className="bg-purple-500/20 text-purple-400 text-xs">
                          <Pin className="w-3 h-3 mr-1" />
                          {t('reels.pinned')}
                        </Badge>
                      ) : (
                        <span className="text-gray-600">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {reel.brands && reel.brands.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {reel.brands.map((brand, bIdx) => (
                            <Badge 
                              key={bIdx} 
                              variant="secondary" 
                              className="bg-yellow-500/20 text-yellow-400 text-xs"
                            >
                              {brand}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-600">-</span>
                      )}
                    </TableCell>
                    {/* v31 Commit-3: Katman-eleme rozeti (K1 floor/ceiling | K2 2.5x-outlier). TAM-shortcode eslesme. */}
                    <TableCell className="text-center">
                      {(anatomy?.k1_removed_shortcodes || []).includes(reel.shortcode) ? (
                        <Badge variant="secondary" className="bg-red-500/20 text-red-400 text-xs" title="Floor/Ceiling elemesi (K1)">K1</Badge>
                      ) : (anatomy?.k2_removed_shortcodes || []).includes(reel.shortcode) ? (
                        <Badge variant="secondary" className="bg-amber-500/20 text-amber-400 text-xs" title="2.5x outlier elemesi (K2)">K2</Badge>
                      ) : (
                        <span className="text-gray-600">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Info className="w-12 h-12 text-gray-500 mb-4" />
              <p className="text-gray-400">{t('reels.no_data')}</p>
            </div>
          )}
        </div>

        {/* Footer with legend */}
        {reelsData && reelsData.length > 0 && (
          <div className="border-t border-gray-700 pt-3 mt-3">
            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <span className="text-green-400">✓</span> {t('reels.legend_html')}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-yellow-400">⚠</span> {t('reels.legend_json')}
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400 text-xs py-0">
                  {t('reels.col_brand')}
                </Badge> {t('reels.legend_collab')}
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="secondary" className="bg-purple-500/20 text-purple-400 text-xs py-0">
                  <Pin className="w-2 h-2 mr-1" />
                  {t('reels.pinned')}
                </Badge> {t('reels.legend_pinned')}
              </div>
              {/* v31 Commit-3: Katman-eleme lejantı */}
              <div className="flex items-center gap-1">
                <Badge variant="secondary" className="bg-red-500/20 text-red-400 text-xs py-0">K1</Badge> Floor/Ceiling elemesi
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="secondary" className="bg-amber-500/20 text-amber-400 text-xs py-0">K2</Badge> 2.5x outlier elemesi
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
